import React, { useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'lodash';
import PropTypes from 'prop-types';

import { Popup } from 'src/components/Popup';
import { useTagsPage as useFetchTags } from 'src/hooks/tag';

import Button from 'src/components/Button';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { components } from 'react-select';
import Styled from 'src/components/JobMobileDataTab/MobilePhotoTagSelectorPopup.styles';
import Loader from 'src/components/Loader';

const NoOptionsMessage = (props) => {
  const { saveAndApplyHandler, selectProps } = props;

  const { t } = useTranslation();

  const onTagAdd = (e) => {
    e.preventDefault();

    const { inputValue } = selectProps || {};
    if (!inputValue) {
      return;
    }

    const tags = [{ label: inputValue, version: null, value: null }];
    saveAndApplyHandler(tags);
  }

  return (
    <components.NoOptionsMessage {...props}>
      <Styled.TagsAddButton outline orange onClick={onTagAdd}>{t('TAGS_ADD_IN_LIST')}</Styled.TagsAddButton>
    </components.NoOptionsMessage>
  );
};

const MobilePhotoTagSelectorPopup = ({ existingTags, onCloseHandler, saveAndApplyHandler, missingTagsConfigurationTagIds }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [isCreatingNewTag, setIsCreatingNewTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState('');
  const [isNewTagError, setIsNewTagError] = useState(true);
  const { data: tagsData, loading: tagsLoading } = useFetchTags({ limit: 10000 });
  const { t } = useTranslation();

  useEffect(()=>{
    setIsNewTagError(true);
    setNewTagValue('');
  },[isCreatingNewTag]);

  const getNonExitingTags = (tags = []) => {
    return tags.filter(tag => {
      return existingTags.findIndex(existingTag => {
        return existingTag.tagId === tag.value || existingTag.name?.trim().toLowerCase() === tag.label?.trim().toLowerCase();
      }) === -1;
    });
  };

  const options = useMemo(() => {
    const tags = tagsData?.tags
      ?.map(tag => ({
        value: tag.id,
        label: tag.currentVersion?.name,
        version: tag.currentVersion?.version
      })) || [];

    if (tags && tags.length > 0) {
      return getNonExitingTags(tags);
    }
    return tags;
  }, [tagsData]);

  const onselect = (val) => {
    if (val) {
      setSelectedTags(val);
      return;
    }
    setSelectedTags([]);
  };

  const newTagValueChangeHandler = (e) => {
    setNewTagValue(e.target.value);
  };

  const validateTagValue = values => {
    const errors = {};
    if (!values.newTagValue) {
      errors.newTagValue = t('TAG_EMPTY_ERR_MSG');
    }
    if (values.newTagValue &&
      existingTags.some(({ name }) => name.trim().toLowerCase() === values.newTagValue.trim().toLowerCase())) {
      errors.newTagValue = t('TAG_SIMILAR_PRESENT_ERR_MSG');
    }
    else if (values.newTagValue &&
      (tagsData?.tags || []).some(({ currentVersion }) => currentVersion?.name?.trim().toLowerCase() === values.newTagValue.trim().toLowerCase())) {
      errors.newTagValue = t('TAG_SIMILAR_AVAILABLE_ERR_MSG');
    }
    if(!errors.newTagValue){
      setIsNewTagError(false);
    }else{
      setIsNewTagError(true);
    }
    return errors;
  };

  const renderNewTagField = () => {
    return (
      <Formik
        initialValues={{ newTagValue }}
        onSubmit={()=>false}
        validate={validateTagValue}>
        {({ values, errors, handleChange }) => <Styled.NewTagTextField
          error={errors.newTagValue}
          label={t('NEW_TAG')}
          name="newTagValue"
          onChange={(e) => {
            handleChange(e);
            newTagValueChangeHandler(e);
          }}
          required={Boolean(errors.newTagValue)}
          value={values.newTagValue} />
        }
      </Formik>
    );
  };

  const addSelectedTag = (tag) => {
    setSelectedTags([
      ...selectedTags,
      tag,
    ]);
  };

  const onSaveAndApplyClickHandler = (e) => {
    e.preventDefault();
    const tags = isCreatingNewTag
      ? [{ label: newTagValue, version: null, value: null }]
      : [...selectedTags];
    saveAndApplyHandler(tags);
  };

  const selectedTagIds = useMemo(() => {
    return selectedTags.map(c => c.value);
  }, [selectedTags]);

  const missingTagIds = missingTagsConfigurationTagIds.filter(tagId => !selectedTagIds.includes(tagId));

  return (
    <Styled.Container>
      <Popup isCloseHidden={false} onClose={onCloseHandler}>
        <Styled.PopupInner onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}>
          {!isCreatingNewTag
            ? <SelectBox
              isMulti={true}
              label={t('TAGS')}
              maxMenuHeight={'5em'}
              onChange={onselect}
              options={options}
              value={selectedTags}
              components={{
                NoOptionsMessage: (inputProps) => (
                  <NoOptionsMessage
                    {...inputProps}
                    saveAndApplyHandler={(newTags) => saveAndApplyHandler([...selectedTags, ...newTags])}
                  />
                ),
              }}
            />
            : renderNewTagField()
          }
          {(missingTagIds.length > 0) && (
            <Styled.RequiredTagsWrapper>
              <strong>{t('TAGS_CONFIGURATION_EXPECTED')}</strong>
              <div>{t('TAGS_CONFIGURATION_EXPECTED_DESCRIPTION')}</div>
              {tagsLoading && (
                <Loader />
              )}
              {orderBy(missingTagIds, [tagId => {
                const tag = options.find(c => c.value === tagId);
                if (!tag) {
                  return null;
                }

                return tag.label;
              }], ['asc']).map(tagId => {
                const tag = options.find(c => c.value === tagId);
                if (!tag) {
                  return null;
                }

                return (
                  <Styled.RequiredTag key={tagId} onClick={() => addSelectedTag(tag)}>
                    {tag.label}
                  </Styled.RequiredTag>
                );
              })}
            </Styled.RequiredTagsWrapper>
          )}
          <Styled.PopupFooter isCreatingNewTag={isCreatingNewTag}>
            {!isCreatingNewTag
              ? <div>
            <span>{t('CREATE_NEW_TAG_HINT')} &nbsp;
              <span onClick={() => setIsCreatingNewTag(true)}>
              <Styled.AddTagIconBtn name="plus" />
              </span>
            </span>
              </div>
              : null
            }

            <Styled.PopupFooterActionBar>
              <Button
                className={Styled.Classes['btn-save-and-apply']}
                disabled={isCreatingNewTag ? isNewTagError : selectedTags.length === 0}
                onClick={(e)=>onSaveAndApplyClickHandler(e)}>
                Save And Apply
              </Button>
              {!isCreatingNewTag ?
                <Styled.LinkButton
                  ghost
                  onClick={(e) => {
                    e.target.blur();
                    onCloseHandler();
                  }}
                  red
                  sm
                  type="button"
                >
                  {t('CANCEL')}
                </Styled.LinkButton>
                :
                <Styled.LinkButton
                  ghost
                  onClick={(e) => {
                    e.target.blur();
                    setIsCreatingNewTag(false);
                  }}
                  red
                  sm
                  type="button"
                >
                  {t('BACK')}
                </Styled.LinkButton>}
            </Styled.PopupFooterActionBar>
          </Styled.PopupFooter>
        </Styled.PopupInner>
      </Popup>
    </Styled.Container>);
};

MobilePhotoTagSelectorPopup.propTypes = {
  existingTags: PropTypes.arrayOf(
    PropTypes.shape({
      tagId: PropTypes.number,
      name: PropTypes.string
    })
  ),
  onCloseHandler: PropTypes.func.isRequired,
  saveAndApplyHandler: PropTypes.func.isRequired,
  missingTagsConfigurationTagIds: PropTypes.arrayOf(PropTypes.number)
};

export default MobilePhotoTagSelectorPopup;
